import React, { useState } from "react";
import { FiX, FiShoppingBag, FiLink } from "react-icons/fi";
import axios from "axios";

const ProductsModal = ({ show, handleClose, onSave }) => {
  const [mode, setMode] = useState("single");
  const [triggerType, setTriggerType] = useState("keyword");
  const [keywords, setKeywords] = useState("");
  const [button, setButton] = useState("");

  // Product Fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [buttonText, setButtonText] = useState("Buy Now");
  const [buttonUrl, setButtonUrl] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(image?.location || null);
  const [imgUploading, setImgUploading] = useState(false);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setImgUploading(true);

      const formData = new FormData();
      formData.append("image", file);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/campaigns/s3-upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const img = res.data.image;

      setImage(img);
      setImagePreview(img.location);
    } catch (err) {
      console.error("Image upload failed", err);
      alert("Image upload failed");
    } finally {
      setImgUploading(false);
    }
  };

  // ---------------------------
  // Save
  // ---------------------------
  const handleSubmit = (e) => {
    e.preventDefault();

    if (mode === "single") {
      if (
        !title ||
        !description ||
        !price ||
        !buttonText ||
        !buttonUrl ||
        !image
      ) {
        alert("Please fill all product fields");
        return;
      }
    }

    const payload = {
      mode,
      triggerType,
      button: triggerType === "button_click" ? button : undefined,
      keywords:
        triggerType === "keyword"
          ? keywords.split(",").map((k) => k.trim().toLowerCase())
          : [],
      product:
        mode === "single"
          ? {
              title,
              description,
              price,
              buttonText,
              buttonUrl,
              image: {
                key: image.key,
                location: image.location,
              },
            }
          : undefined,
    };

    onSave(payload);
    handleClose();
  };

  return (
    <div
      className={`modal fade ${show ? "show d-block" : ""}`}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div
          className="modal-content border-0 shadow"
          style={{ overflowY: "auto" }}
        >
          <div className="modal-header bg-success text-white">
            <h5 className="modal-title">
              <FiShoppingBag className="me-2" />
              Send Product
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Mode Selector */}
              <div className="mb-4">
                <label className="form-label fw-bold d-block mb-2">Mode</label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="form-select form-select-lg"
                >
                  <option value="single">Single Product</option>
                  <option value="collection" disabled>
                    Collection (Coming Soon)
                  </option>
                </select>
              </div>

              {/* Trigger Type */}
              <div className="mb-4">
                <label className="form-label fw-bold d-block mb-2">
                  Trigger Type
                </label>
                <select
                  value={triggerType}
                  onChange={(e) => setTriggerType(e.target.value)}
                  className="form-select form-select-lg"
                >
                  <option value="keyword">Keyword Match</option>
                  <option value="button_click">Button Click</option>
                </select>
              </div>

              {/* Keywords */}
              {triggerType === "keyword" && (
                <div className="mb-4">
                  <label className="form-label fw-bold d-block mb-2">
                    Keywords (comma separated)
                  </label>
                  <input
                    type="text"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    className="form-control"
                    placeholder="e.g. buy, shop, price"
                  />
                </div>
              )}

              {/* Button Name */}
              {triggerType === "button_click" && (
                <div className="mb-4">
                  <label className="form-label fw-bold d-block mb-2">
                    Button Name
                  </label>
                  <input
                    type="text"
                    value={button}
                    onChange={(e) => setButton(e.target.value)}
                    className="form-control"
                    placeholder="e.g. shop"
                  />
                </div>
              )}

              {/* ------------------------------
                    SINGLE PRODUCT FIELDS
                  ------------------------------ */}
              {mode === "single" && (
                <div className="border-top pt-4 mt-4">
                  <h6 className="fw-bold mb-3">Product Details</h6>

                  <div className="mb-4">
                    <label className="form-label fw-bold d-block mb-2">
                      Product Title
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="form-control"
                      required={mode === "single"}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold d-block mb-2">
                      Product Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={200}
                      className="form-control"
                      rows={3}
                      required={mode === "single"}
                    />
                    <small className="text-muted mt-1">
                      {description.length}/200 characters
                    </small>
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold d-block mb-2">
                      Price
                    </label>
                    <input
                      type="text"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="form-control"
                      required={mode === "single"}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold d-block mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      className="form-control"
                      required={mode === "single"}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold d-block mb-2">
                      Button URL
                    </label>
                    <input
                      type="url"
                      value={buttonUrl}
                      onChange={(e) => setButtonUrl(e.target.value)}
                      className="form-control"
                      placeholder="https://example.com/product"
                      required={mode === "single"}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold d-block mb-2">
                      Product Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="form-control"
                      required={mode === "single"}
                    />

                    {imgUploading && (
                      <div className="alert alert-info mt-2 py-2">
                        <small>Uploading image...</small>
                      </div>
                    )}

                    {imagePreview && (
                      <div className="mt-3">
                        <p className="text-muted mb-1">Preview:</p>
                        <img
                          src={imagePreview}
                          alt="preview"
                          className="img-thumbnail"
                          style={{ maxWidth: "200px", maxHeight: "200px" }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer border-top-0">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-success">
                Save Configuration
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductsModal;
